import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { PauseFilled, PlayFilled } from '@landr/maestro';
import { t } from '@lingui/macro';
import { ActionButton } from 'components/ActionButton';
import { withErrorBoundary } from 'error';
import { noop } from 'utils/noop';
import { useDebouncedState } from 'hooks/useDebouncedState/useDebouncedState';
import { usePlayerContext } from 'contexts/PlayerContext/PlayerContext';
import { PlayerButtonProps, UsePlayerButtonProps } from './types';

const usePlayerButton = ({
    interactWithPlayer,
    keyboardEventConsumerRef,
    loading,
    playing,
    ref,
    preventDefault,
    stopPropagation,
    totalTime,
    callback,
    onClick,
    togglePlaying,
    ButtonComponent,
}: UsePlayerButtonProps) => {
    const PauseLabel = t`Pause`;
    const PlayLabel = t`Play`;

    const buttonLabel = playing ? PauseLabel : PlayLabel;

    // CALLBACK
    const handleButtonClick = useCallback(
        (event: React.MouseEvent) => {
            preventDefault && event.preventDefault();
            stopPropagation && event.stopPropagation();

            const element = keyboardEventConsumerRef?.current || ref?.current;

            element?.focus();

            interactWithPlayer && togglePlaying();

            onClick(!playing);

            !playing && callback?.(totalTime);
        },
        [
            preventDefault,
            stopPropagation,
            keyboardEventConsumerRef,
            ref,
            interactWithPlayer,
            togglePlaying,
            onClick,
            playing,
            callback,
            totalTime,
        ],
    );

    // MEMO
    const CustomButtonComponent = useMemo(() => {
        return ButtonComponent ? (
            <ButtonComponent
                playing={playing}
                isLoading={loading}
                onClick={handleButtonClick}
            />
        ) : null;
    }, [ButtonComponent, playing, loading, handleButtonClick]);

    return { buttonLabel, CustomButtonComponent, handleButtonClick };
};

const PlayerButton = ({
    id,
    ButtonComponent,
    callback,
    preventDefault = true,
    stopPropagation = true,
    label,
    onClick = noop,
    isDemo = false,
    keyboardEventConsumerRef,
    tabIndex,
    interactWithPlayer = true,
    isPackPreview = false,
    ...props
}: PlayerButtonProps) => {
    const buttonRef = useRef<HTMLDivElement>(null);
    const { loading, playing, togglePlaying, totalTime, seek } =
        usePlayerContext();

    const loadingDebounced = useDebouncedState(loading);

    // re-init waveform currentTime on mount
    useEffect(() => {
        seek && seek(0, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { buttonLabel, CustomButtonComponent, handleButtonClick } =
        usePlayerButton({
            interactWithPlayer,
            keyboardEventConsumerRef,
            loading: loadingDebounced,
            playing,
            ref: buttonRef,
            preventDefault,
            stopPropagation,
            totalTime,
            callback,
            onClick,
            togglePlaying,
            ButtonComponent,
        });

    if (ButtonComponent) {
        return CustomButtonComponent;
    }

    return (
        <ActionButton
            ref={buttonRef}
            aria-label={buttonLabel}
            label={isPackPreview ? '' : buttonLabel}
            icon={playing ? PauseFilled : PlayFilled}
            isLoading={loadingDebounced}
            onClick={handleButtonClick}
            preventDefault={preventDefault}
            stopPropagation={stopPropagation}
            tabIndex={tabIndex}
            {...props}
        />
    );
};

PlayerButton.displayName = 'PlayerButton';

export default withErrorBoundary<PlayerButtonProps>(PlayerButton, 'minimal');
